/**
 * THE SHELVES — the book in the house's own order, a page at a time.
 * OWNER: web-frontend-engineer. Written 5 September 2026, finding C4 of the
 * STYLIST-1 fix wave (the ruling is in DESIGN_RULINGS.md under "STYLIST-1 FIX
 * WAVE"). The paging only: the consultation itself is STYLIST-2.
 *
 * ── THE DEFECT THIS EXISTS FOR ────────────────────────────────────────────
 *
 * /book rendered the whole catalogue on one page — 1,798 cloths, 1,796 <img>
 * elements, 313,578 px tall — with the first viewport given to five columns
 * of radio options and the same per-card act repeated 1,798 times. The demo's
 * book has never done this: it opens on a SHELF (navy first) and the rest of
 * the book is a shelf away.
 *
 * ── WHAT THIS MODULE DECIDES ──────────────────────────────────────────────
 *
 *   1. THE SHELVES, in the house order the demo's book uses:
 *      navy · charcoal · grey · blues · brown · green · burgundy · black and
 *      midnight · stone · ivory · statement. `HOUSE_SHELVES` is that list and
 *      nothing else in the web app carries a copy of it.
 *   2. WHICH SHELF A CLOTH IS ON, from the one field the client payload
 *      carries for it: `colourName`. `tools/import-real-cloths.mjs` writes each
 *      real cloth's colour name FROM its shelf (`COLOUR_WORD` — exactly one
 *      word per shelf), so for the 1,774 photographed cloths this is an exact
 *      inversion. The 24 placeholder rows carry a mill's own names ("Midnight
 *      Blue", "Mid Grey", "Peat Brown"); for those the first word that names a
 *      shelf decides. `tests/web/book-pages.test.ts` holds every colour name
 *      in rules.json to a shelf, so a cloth cannot arrive shelfless unnoticed.
 *   3. THE PAGE. Twenty-four to a page — six rows of the four-column grid at
 *      the desk, twelve of the phone's two — clamped, in the server's own
 *      order within a shelf. Revealed Tier-0 cloths are APPENDED after the
 *      shelf's offered cloths in the same sequence (COMPONENT_SPEC §4.2: never
 *      interleaved, never re-sorted in), so turning the reveal on moves no
 *      tile that was already showing; it lengthens the sequence.
 *
 * Pure. No React, no copy — the words are the catalogue's and are resolved
 * where they are rendered.
 */

import type { ClothSummary } from './contracts.ts';

/* STYLIST-2 (6 September 2026): the shelves and the colour-name inversion moved
   into @pt/core (domain/shelves.ts) so the consultation engine and this paging
   read ONE list; re-exported here so every importer keeps its address. */
export { HOUSE_SHELVES, SHELF_WORDS, shelfOf, type HouseShelf } from '@pt/core';
import { HOUSE_SHELVES, shelfOf, type HouseShelf } from '@pt/core';
/** A shelf, or the whole book in shelf order. */
export type BookShelf = HouseShelf | 'all';

type Shelved = Pick<ClothSummary, 'colourName'>;

/**
 * The rows on one shelf, in the order they arrived. For `'all'`, every row in
 * shelf order — stable within a shelf — with any shelfless row last.
 */
export function onShelf<Row extends Shelved>(rows: readonly Row[], shelf: BookShelf): Row[] {
  if (shelf !== 'all') return rows.filter((row) => shelfOf(row.colourName) === shelf);
  const rank = (row: Row): number => {
    const found = shelfOf(row.colourName);
    return found === null ? HOUSE_SHELVES.length : HOUSE_SHELVES.indexOf(found);
  };
  return rows
    .map((row, index) => ({ row, index, rank: rank(row) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ row }) => row);
}

/** How many cloths each shelf holds, offered and hidden together — the facet's own count. */
export function shelfCounts(
  offered: readonly Shelved[],
  hidden: readonly Shelved[],
): Readonly<Record<BookShelf, number>> {
  const counts = Object.fromEntries(HOUSE_SHELVES.map((shelf) => [shelf, 0])) as Record<BookShelf, number>;
  counts.all = 0;
  for (const row of [...offered, ...hidden]) {
    counts.all += 1;
    const shelf = shelfOf(row.colourName);
    if (shelf !== null) counts[shelf] += 1;
  }
  return counts;
}

/** The shelf the book opens on: the first in house order that holds a cloth. */
export function defaultShelf(counts: Readonly<Record<BookShelf, number>>): BookShelf {
  return HOUSE_SHELVES.find((shelf) => counts[shelf] > 0) ?? 'all';
}

/** The shelves the chips show: those that hold a cloth, the chosen one regardless, and the whole book. */
export function visibleShelves(
  counts: Readonly<Record<BookShelf, number>>,
  chosen: BookShelf,
): readonly BookShelf[] {
  return [...HOUSE_SHELVES.filter((shelf) => counts[shelf] > 0 || shelf === chosen), 'all'];
}

export const BOOK_PAGE_SIZE = 24;

export interface BookView {
  readonly shelf: BookShelf;
  readonly page: number;
  /** Tier-0 cloths revealed (M-5): appended after the shelf's offered cloths. */
  readonly revealed: boolean;
}

export interface BookPage<Row> {
  /** The offered cloths on this page. */
  readonly rows: readonly Row[];
  /** The revealed Tier-0 cloths on this page — empty unless revealed. */
  readonly hiddenRows: readonly Row[];
  /** Everything the pager counts: offered, plus hidden when revealed. */
  readonly total: number;
  /** The shelf's offered cloths, whole — what the Tier-0 statement names. */
  readonly offeredTotal: number;
  /** The shelf's Tier-0 cloths, whole, revealed or not. */
  readonly hiddenTotal: number;
  readonly fromRow: number;
  readonly toRow: number;
  readonly page: number;
  readonly pageCount: number;
}

export function bookPage<Row extends Shelved>(
  offered: readonly Row[],
  hidden: readonly Row[],
  view: BookView,
): BookPage<Row> {
  const shelfRows = onShelf(offered, view.shelf);
  const shelfHidden = onShelf(hidden, view.shelf);
  const sequenceHidden = view.revealed ? shelfHidden : [];
  const total = shelfRows.length + sequenceHidden.length;
  const pageCount = Math.max(1, Math.ceil(total / BOOK_PAGE_SIZE));
  const page = Math.min(Math.max(1, Math.trunc(view.page)), pageCount);
  const start = (page - 1) * BOOK_PAGE_SIZE;
  const end = start + BOOK_PAGE_SIZE;
  const rows = shelfRows.slice(start, end);
  const hiddenRows = sequenceHidden.slice(
    Math.max(0, start - shelfRows.length),
    Math.max(0, end - shelfRows.length),
  );
  return {
    rows,
    hiddenRows,
    total,
    offeredTotal: shelfRows.length,
    hiddenTotal: shelfHidden.length,
    fromRow: total === 0 ? 0 : start + 1,
    toRow: start + rows.length + hiddenRows.length,
    page,
    pageCount,
  };
}
